
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PlusIcon, TrashIcon, PlusCircleIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import { truckingBillAPI, collectionAPI } from '../../api/waybillAPI';
import WaybillSearch from '../../components/WaybillSearch';
import useGuardedMutation from '../../hooks/useGuardedMutation';

function GLStatusBadge({ status, error, collectionId, isVoided, onRetry, loading, disabled }) {
  if (isVoided) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-200 text-gray-600">
        VOID
      </span>
    );
  }
  if (status === 'posted') {
    return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">Posted</span>;
  }
  if (status === 'failed') {
    return (
      <div className="flex items-center gap-1">
        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700" title={error || 'GL posting failed'}>
          Failed
        </span>
        <button
          onClick={() => onRetry(collectionId)}
          disabled={disabled || loading}
          className="text-xs text-teal-600 hover:underline whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {loading ? 'Posting…' : 'Retry'}
        </button>
      </div>
    );
  }
  return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">Pending</span>;
}
  
const PHP = (n) => new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmt = (d) => d ? new Date(d).toLocaleDateString('en-PH') : '—';
const today = () => new Date().toISOString().split('T')[0];

const emptyForm = {
  BillName: '', BillDate: today(), BillDueDate: '', SOANumber: '',
  paymentTerm: '',
  VendorDONumber: '', VendorInvoiceNumber: '',
  CarrierName: '', CarrierAddress: '',
  isPSACC: false,
  IsReceived: false, BillTypeId: 2,
};

export default function TruckingBillingEditPage() {
  const { id } = useParams();
  const isAdd = !id;
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  // Last persisted version of `form`, used only to tell whether the PSACC print
  // would show stale figures. Line edits are not tracked here: add/remove line
  // and date-return each hit their own endpoint immediately, so they are never
  // pending. Set on load and after every successful save.
  const [savedForm, setSavedForm] = useState(emptyForm);
  const [lines, setLines] = useState([]);
  const [selectedWaybillId, setSelectedWaybillId] = useState('');
  const [selectedWaybillObj, setSelectedWaybillObj] = useState(null);
  const [usageWarning, setUsageWarning] = useState(null); // { billId, billName } | null
  const [checkingUsage, setCheckingUsage] = useState(false);
  const [addingLine, setAddingLine] = useState(false);
  const [loading, setLoading] = useState(!isAdd);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [showPSACCPrintModal, setShowPSACCPrintModal] = useState(false);
  const [psaccSaving, setPsaccSaving] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [isVoided, setIsVoided] = useState(false);
  const [voidedInfo, setVoidedInfo] = useState({ at: null, reason: '' });

  // Collections tab state
  const [collections, setCollections]               = useState([]);
  const [collectionsFetched, setCollectionsFetched] = useState(false);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [showPaymentForm, setShowPaymentForm]       = useState(false);
  const [paymentForm, setPaymentForm]               = useState({
    transactionType: 'payment', deductionType: 'discount', withheldAmount: '',
    amount: '', paymentDate: today(), paymentMethod: 'cash', referenceNo: '', notes: '',
  });

  function computeDueDate(waybillDateValue, term) {
    if (!waybillDateValue || !term) return '';
    const base = new Date(waybillDateValue);
    if (isNaN(base.getTime())) return '';
    const days = { immediate: 0, net15: 15, net30: 30, net45: 45 };
    const offset = days[term];
    if (offset === undefined) return '';
    const due = new Date(base);
    due.setDate(due.getDate() + offset);
    return due.toISOString().split('T')[0];
  }

  function getEarliestWaybillDate(sourceLines) {
    return sourceLines.length > 0
      ? sourceLines.reduce((earliest, l) => {
          const d = l.WayBillId?.TransactionDate || l.WayBillId?.transactionDate;
          if (!d) return earliest;
          return !earliest || new Date(d) < new Date(earliest) ? d : earliest;
        }, null)
      : null;
  }

  useEffect(() => {
    if (isAdd) {
      truckingBillAPI.nextNumber(2)
        .then((r) => setForm((p) => ({ ...p, BillName: r.data.number })))
        .catch(() => {});
      // No SOA Number is reserved here on purpose. Reserving on mount consumed
      // a number every time the form was opened, whether or not the bill was
      // ever saved, so abandoned drafts burned numbers out of a sequence that
      // has to stay explainable. The bill's SOANumber is now assigned once, at
      // save, by the TruckingBill pre-save hook — leaving the field blank means
      // "assign the next one". Typing a value here still overrides it.
    }

    if (!isAdd) {
      truckingBillAPI.getById(id).then((r) => {
        const { bill, lines: l } = r.data;
        const loaded = {
          BillName: bill.BillName || '',
          BillDate: bill.BillDate ? bill.BillDate.split('T')[0] : today(),
          BillDueDate: bill.BillDueDate ? bill.BillDueDate.split('T')[0] : '',
          paymentTerm: bill.paymentTerm || '',
          SOANumber: bill.SOANumber || '',
          VendorDONumber: bill.VendorDONumber || '',
          VendorInvoiceNumber: bill.VendorInvoiceNumber || '',
          CarrierName: bill.CarrierName || '',
          CarrierAddress: bill.CarrierAddress || '',
          isPSACC: !!bill.isPSACC,
          IsReceived: !!bill.IsReceived,
          BillTypeId: 2,
        };
        setForm(loaded);
        setSavedForm(loaded);
        setIsVoided(!!bill.isVoided);
        if (bill.isVoided) setVoidedInfo({ at: bill.voidedAt, reason: bill.voidReason || '' });
        setLines(l || []);
        setLoading(false);
      }).catch(() => { setError('Failed to load bill.'); setLoading(false); });
    }
  }, [id, isAdd]);

  const set = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [field]: val }));
  };

  // Real-time, best-effort check as soon as a waybill is selected — the
  // hard guarantee is still the server-side check in addWaybillLine below.
  useEffect(() => {
    if (!selectedWaybillId) { setUsageWarning(null); return; }
    let cancelled = false;
    setCheckingUsage(true);
    truckingBillAPI.checkWaybillUsage(selectedWaybillId)
      .then((r) => {
        if (cancelled) return;
        const data = r.data;
        setUsageWarning(data?.inUse ? { billId: data.billId, billName: data.billName } : null);
      })
      .catch(() => { if (!cancelled) setUsageWarning(null); })
      .finally(() => { if (!cancelled) setCheckingUsage(false); });
    return () => { cancelled = true; };
  }, [selectedWaybillId]);

  const handleAddLine = async () => {
    if (!selectedWaybillId || !id || addingLine) return;
    setError('');
    setAddingLine(true);
    try {
      const res = await truckingBillAPI.addWaybillLine(id, selectedWaybillId);
      const newLine = res.data?.data || res.data;
      setLines((prev) => {
        const nextLines = [...prev, newLine];
        if (form.paymentTerm) {
          const nextWaybillDate = getEarliestWaybillDate(nextLines);
          const due = computeDueDate(nextWaybillDate, form.paymentTerm);
          if (due) setForm((p) => ({ ...p, BillDueDate: due }));
        }
        return nextLines;
      });
      setSelectedWaybillId('');
      setSelectedWaybillObj(null);
    } catch (e) {
      const data = e?.response?.data;
      setError(data?.message || 'Failed to add waybill line.');
      // Server is the source of truth — reflect a 409 conflict inline even
      // if the earlier real-time check missed it (stale dropdown, race).
      if (e?.response?.status === 409 && data?.billId) {
        setUsageWarning({ billId: data.billId, billName: data.billName });
      }
    } finally {
      setAddingLine(false);
    }
  };

  const handleRemoveLine = async (lineId) => {
    if (!window.confirm('Remove this line?')) return;
    try {
      await truckingBillAPI.removeLine(id, lineId);
      setLines((prev) => {
        const nextLines = prev.filter((l) => l._id !== lineId);
        if (form.paymentTerm) {
          const nextWaybillDate = getEarliestWaybillDate(nextLines);
          const due = computeDueDate(nextWaybillDate, form.paymentTerm);
          if (due) setForm((p) => ({ ...p, BillDueDate: due }));
        }
        return nextLines;
      });
    } catch (e) {
      setError('Failed to remove line.');
    }
  };

  const handleVoid = async () => {
    if (!voidReason.trim()) return;
    setVoiding(true);
    try {
      await truckingBillAPI.voidBill(id, voidReason.trim());
      setIsVoided(true);
      setVoidedInfo({ at: new Date().toISOString(), reason: voidReason.trim() });
      setShowVoidModal(false);
      setVoidReason('');
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || 'Failed to void bill.');
    } finally {
      setVoiding(false);
    }
  };

  const { mutate: handleSave, isPending: saving } = useGuardedMutation(async () => {
    if (isVoided) return;
    setError('');
    try {
      if (isAdd) {
        const r = await truckingBillAPI.create(form);
        navigate(`/billing/trucking-billing/${r.data.bill._id}/edit`);
      } else {
        await truckingBillAPI.update(id, form);
        setSavedForm(form);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to save.');
      throw e; // let useGuardedMutation know this attempt failed — keeps the idempotency key for retry
    }
  });

  const handlePrint = async () => {
    try {
      const res   = await truckingBillAPI.getTruckingPrintToken(id);
      const token = res.data?.printToken;
      if (token) window.open(`/billing/reports/trucking-billing/${token}`, '_blank');
    } catch (e) {
      console.error('Print token failed', e);
    }
  };

  // ─── PSACC print ───────────────────────────────────────────────────────────
  // Both PSACC statements read the SAVED bill, so pending header edits would
  // print stale. Rather than silently saving (which would change what the
  // existing Print Trucking Billing button does by association), the user is
  // asked. Note this only guards the header fields in `form`; line changes are
  // already persisted when they are made.
  const psaccDirty = !isAdd && JSON.stringify(form) !== JSON.stringify(savedForm);

  const openPSACCStatements = () => {
    // One window, one print job: the billing statement and the additional-
    // stripping statement are issued together on consecutive SOA Numbers, so
    // they are printed as a single document with a page break between them.
    const win = window.open(`/billing/reports/print/psacc-combined/${id}`, '_blank');
    if (!win) {
      setError('Your browser blocked the print window. Allow pop-ups for this site, then print again.');
    }
  };

  const handlePrintPSACC = () => {
    setError('');
    if (psaccDirty) { setShowPSACCPrintModal(true); return; }
    openPSACCStatements();
  };

  const handlePSACCPrintAnyway = () => {
    setShowPSACCPrintModal(false);
    openPSACCStatements();
  };

  const handlePSACCSaveAndPrint = async () => {
    setError('');
    setPsaccSaving(true);
    try {
      await truckingBillAPI.update(id, form);
      setSavedForm(form);
      setShowPSACCPrintModal(false);
      openPSACCStatements();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to save. Nothing was printed.');
      setShowPSACCPrintModal(false);
    } finally {
      setPsaccSaving(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'collections' && !collectionsFetched && id) {
      setCollectionsLoading(true);
      collectionAPI.getByBill(id)
        .then((res) => {
          setCollections(Array.isArray(res.data?.data) ? res.data.data : []);
          setCollectionsFetched(true);
        })
        .catch(() => {})
        .finally(() => setCollectionsLoading(false));
    }
  };

  const waybillDate = lines.length > 0
    ? lines.reduce((earliest, l) => {
        const d = l.WayBillId?.TransactionDate || l.WayBillId?.transactionDate;
        if (!d) return earliest;
        return !earliest || new Date(d) < new Date(earliest) ? d : earliest;
      }, null)
    : null;
  const sumSubTotal = lines.reduce((s, l) => s + (l.SubTotal || 0), 0);
  const grandTotal = sumSubTotal;

  if (loading) {
    return <Layout pageTitle="Trucking Billing"><div className="flex justify-center items-center h-48 text-gray-400">Loading…</div></Layout>;
  }

  return (
    <Layout pageTitle={isAdd ? 'New Trucking Bill' : `Trucking Bill — ${form.BillName}`}>
      <div className="space-y-6 max-w-5xl">
        {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-3 text-sm">Bill saved successfully.</div>}
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>}

        {/* Tab bar */}
        {!isAdd && (
          <div className="border-b border-gray-200">
            <nav className="flex gap-0" aria-label="Tabs">
              {[
                { key: 'details',     label: 'Details' },
                { key: 'collections', label: 'Collections' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-teal-600 text-teal-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        )}

        {/* COLLECTIONS tab */}
        {!isAdd && activeTab === 'collections' && (
          <TruckingCollectionsPanel
            id={id}
            amountDue={grandTotal}
            isVoided={isVoided}
            form={form}
            collections={collections}
            setCollections={setCollections}
            collectionsLoading={collectionsLoading}
            showPaymentForm={showPaymentForm}
            setShowPaymentForm={setShowPaymentForm}
            paymentForm={paymentForm}
            setPaymentForm={setPaymentForm}
            setForm={setForm}
            setError={setError}
          />
        )}

        {/* DETAILS tab */}
        {(isAdd || activeTab === 'details') && (
          <>
        {/* Voided banner */}
        {!isAdd && isVoided && (
          <div className="flex items-center gap-3 bg-red-50 border border-red-300 text-red-700 rounded-lg px-4 py-3 text-sm">
            <NoSymbolIcon className="h-5 w-5 shrink-0" />
            <div>
              <span className="font-semibold">This bill has been voided.</span>
              {voidedInfo.reason && <span className="ml-2">Reason: {voidedInfo.reason}</span>}
              {voidedInfo.at && <span className="ml-2 text-red-500">{new Date(voidedInfo.at).toLocaleDateString('en-PH', { dateStyle: 'medium' })}</span>}
            </div>
          </div>
        )}

        {/* Section 1 — Primary bill details */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">Bill Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <F label="Bill No." value={form.BillName} onChange={set('BillName')} />
            <F label="Bill Date" type="date" value={form.BillDate} onChange={set('BillDate')} />
            <F label="Due Date" type="date" value={form.BillDueDate} onChange={set('BillDueDate')} />
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Payment Terms</label>
              <select
                value={form.paymentTerm}
                onChange={(e) => {
                  const term = e.target.value;
                  const base = waybillDate || form.BillDate;
                  const due = computeDueDate(base, term);
                  setForm((p) => ({
                    ...p,
                    paymentTerm: term,
                    BillDueDate: due || p.BillDueDate,
                  }));
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">— Select Terms —</option>
                <option value="immediate">Due Immediately</option>
                <option value="net15">Net 15 days</option>
                <option value="net30">Net 30 days</option>
                <option value="net45">Net 45 days</option>
              </select>
              {form.paymentTerm && (waybillDate || form.BillDate) && (
                <p className="text-xs text-gray-400 mt-1">
                  Based on {waybillDate ? 'waybill' : 'bill'} date:{' '}
                  {new Date(waybillDate || form.BillDate).toLocaleDateString('en-PH', {
                    month: 'short', day: 'numeric', year: 'numeric'
                  })}
                </p>
              )}
            </div>
            <F label="Trucking Name" value={form.VendorDONumber} onChange={set('VendorDONumber')} />
            <F label="Address" value={form.VendorInvoiceNumber} onChange={set('VendorInvoiceNumber')} />
            <div className="flex items-center gap-2 pt-5">
              <CB label="Is Received" checked={form.IsReceived} onChange={set('IsReceived')} />
            </div>
          </div>
        </div>

        {/* Section 2 — PSACC Billing Details (collapsible) */}
        <details className="bg-white rounded-lg shadow" open>
          <summary className="px-6 py-4 text-sm font-semibold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-50 list-none flex items-center justify-between">
            PSACC Billing Details
            <span className="text-xs font-normal text-gray-400">(optional — for carrier billing)</span>
          </summary>
          <div className="px-6 pt-2">
            {/* Marks the bill as billed to the carrier. Gates the PSACC print
                action below and nothing else — the fields in this section stay
                editable either way, and no report changes behaviour. */}
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.isPSACC}
                onChange={set('isPSACC')}
                className="h-4 w-4 mt-0.5 text-teal-600 rounded"
              />
              <span>
                This bill is for PSACC
                <span className="block text-xs text-gray-500">
                  Enables “Print PSACC Billing”, which issues the billing and additional-stripping statements together.
                </span>
              </span>
            </label>
          </div>
          <div className="px-6 pb-6 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <F
              label="SOA Number (YYYY-NNN)"
              value={form.SOANumber}
              onChange={set('SOANumber')}
              placeholder={isAdd ? 'Assigned on save' : ''}
            />
            <F label="Carrier Name" value={form.CarrierName} onChange={set('CarrierName')} />
            <div className="sm:col-span-2">
              <F label="Carrier Address" value={form.CarrierAddress} onChange={set('CarrierAddress')} />
            </div>
          </div>
        </details>

        {/* Add Waybill Line selector — edit mode only */}
        {!isAdd && (
          <>
            {!isVoided && (
            <div className="bg-white rounded-lg shadow p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Add Waybill Trip</h2>
                <a
                  href="/billing/trucking-waybill/add"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1 text-xs font-medium text-teal-600 hover:underline"
                >
                  <PlusCircleIcon className="h-4 w-4" /> New Waybill ↗
                </a>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <WaybillSearch
                    value={selectedWaybillObj}
                    onChange={(w) => {
                      setSelectedWaybillObj(w || null);
                      setSelectedWaybillId(w?._id || '');
                    }}
                  />
                </div>
                <button
                  onClick={handleAddLine}
                  disabled={!selectedWaybillId || !!usageWarning || addingLine}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 disabled:opacity-50"
                >
                  <PlusIcon className="h-4 w-4" /> {addingLine ? 'Adding…' : 'Add Waybill Line'}
                </button>
              </div>
              {checkingUsage && (
                <p className="text-xs text-gray-400">Checking waybill usage…</p>
              )}
              {usageWarning && (
                <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-300 text-yellow-800 rounded-lg px-3 py-2 text-xs">
                  <NoSymbolIcon className="h-4 w-4 shrink-0" />
                  <span>
                    This waybill is already on billing <b>{usageWarning.billName}</b>. Void that billing first to reuse it here.
                  </span>
                </div>
              )}
            </div>
            )}

            {/* Lines table */}
            <div className="bg-white rounded-lg shadow p-6 space-y-4">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Waybill Trip Lines</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Date', 'Ref No.', 'Plate No.', 'Vessel', 'Voyage', 'Van No.', 'Consignee', 'Delivery Address', 'UOM', 'Trucking Rate', 'Stripping Fee', "Add'l Stripping Fee", 'Total', ''].map((h) => (
                        <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {lines.length === 0 ? (
                      <tr><td colSpan={14} className="px-3 py-6 text-center text-gray-400">No lines yet. Add a waybill above.</td></tr>
                    ) : (
                      lines.map((l) => {
                        const wb = l.WayBillId || {};
                        const stripping = wb.StrippingFee || 0;
                        const addlStripping = wb.AdditionalStrippingFee || 0;
                        const total = l.SubTotal || 0;
                        const truckingRate = total - stripping - addlStripping;
                        return (
                          <tr key={l._id} className="hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap">{fmt(wb.TransactionDate)}</td>
                            <td className="px-3 py-2">{wb.referenceNumber || '—'}</td>
                            <td className="px-3 py-2">{wb.PlateNo || '—'}</td>
                            <td className="px-3 py-2">{wb.Vessel || '—'}</td>
                            <td className="px-3 py-2">{wb.Voyage || '—'}</td>
                            <td className="px-3 py-2">{wb.VanNo || '—'}</td>
                            <td className="px-3 py-2">{wb.CustomerId?.CustomerName || wb.CustomerId?.name || '—'}</td>
                            <td className="px-3 py-2">{wb.CustomerAddress || '—'}</td>
                            <td className="px-3 py-2">{l.UnitOfMeasureId?.UnitOfMeasureName || '—'}</td>
                            <td className="px-3 py-2 text-right">₱{PHP(truckingRate)}</td>
                            <td className="px-3 py-2 text-right">₱{PHP(stripping)}</td>
                            <td className="px-3 py-2 text-right">₱{PHP(addlStripping)}</td>
                            <td className="px-3 py-2 text-right font-medium">₱{PHP(total)}</td>
                            <td className="px-3 py-2">
                              {!isVoided && (
                                <button onClick={() => handleRemoveLine(l._id)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Grand Total */}
              <div className="flex justify-end">
                <div className="bg-gray-50 rounded-lg px-6 py-3 text-sm space-y-1 min-w-[16rem]">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span>₱{PHP(sumSubTotal)}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-gray-900 pt-1 border-t border-gray-200">
                    <span>Grand Total</span>
                    <span>₱{PHP(grandTotal)}</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        </>
        )}

        {/* Actions */}
        {(isAdd || activeTab === 'details') && (
        <div className="flex items-center gap-3 flex-wrap">
          <Btn primary onClick={handleSave} disabled={saving || isVoided} label={saving ? 'Saving…' : 'Save'} />
          {!isAdd && <Btn onClick={handlePrint} label="Print Trucking Billing" />}
          {/* Gated on the SAVED flag, not the live form value: the statements
              read the persisted bill, so offering the action for a tick the
              user hasn't saved yet would print a non-PSACC bill. */}
          {!isAdd && savedForm.isPSACC && (
            <Btn onClick={handlePrintPSACC} label="Print PSACC Billing" />
          )}
          {!isAdd && !isVoided && (
            <button
              onClick={() => setShowVoidModal(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 transition-colors"
            >
              <NoSymbolIcon className="h-4 w-4" />
              Void Bill
            </button>
          )}
          <Btn onClick={() => navigate('/billing/trucking-billing')} label="Back" />
        </div>
        )}
      </div>

      {/* Unsaved-changes warning — new button only. Print Trucking Billing keeps
          its existing behaviour of printing whatever is already saved. */}
      {showPSACCPrintModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6 space-y-4">
            <h3 className="text-base font-semibold text-gray-900">You have unsaved changes</h3>
            <p className="text-sm text-gray-600">
              The PSACC statements print from the saved bill, so your unsaved edits
              won’t appear. Print anyway, or save first?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowPSACCPrintModal(false)}
                disabled={psaccSaving}
                className="px-4 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handlePSACCPrintAnyway}
                disabled={psaccSaving}
                className="px-4 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
              >
                Print Anyway
              </button>
              <button
                onClick={handlePSACCSaveAndPrint}
                disabled={psaccSaving}
                className="px-4 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 font-medium"
              >
                {psaccSaving ? 'Saving…' : 'Save & Print'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Void Modal */}
      {showVoidModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6 space-y-4">
            <div className="flex items-center gap-3">
              <NoSymbolIcon className="h-6 w-6 text-red-600" />
              <h3 className="text-base font-semibold text-gray-900">Void Bill</h3>
            </div>
            <p className="text-sm text-gray-600">This action cannot be undone. The bill will be marked as voided and saved to the audit trail.</p>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Reason for voiding <span className="text-red-500">*</span></label>
              <textarea
                rows={3}
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder="Enter reason…"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => { setShowVoidModal(false); setVoidReason(''); }} className="px-4 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200">Cancel</button>
              <button
                onClick={handleVoid}
                disabled={voiding || !voidReason.trim()}
                className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 font-medium"
              >
                {voiding ? 'Voiding…' : 'Confirm Void'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function TruckingCollectionsPanel({
  id, amountDue, isVoided, form, collections, setCollections, collectionsLoading,
  showPaymentForm, setShowPaymentForm, paymentForm, setPaymentForm,
  setForm, setError,
}) {
  const totalCollected = collections.reduce((s, c) => s + c.amount, 0);
  const outstanding    = Math.max((amountDue || 0) - totalCollected, 0);
  // Guards the retry/void GL actions below — one collectionId in flight at a time,
  // mirrors the waybill GL retry button's `glPosting` guard (WaybillEditPage.jsx).
  const [glActionPendingId, setGlActionPendingId] = useState(null);

  const { mutate: handleAddPayment, isPending: paymentSaving } = useGuardedMutation(async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) return;
    try {
      const isDeduction = paymentForm.transactionType === 'deduction';
      const payload = {
        billId:          id,
        amount:          parseFloat(paymentForm.amount),
        paymentDate:     paymentForm.paymentDate,
        transactionType: paymentForm.transactionType,
        notes:           paymentForm.notes,
      };
      if (isDeduction) {
        payload.deductionType = paymentForm.deductionType;
      } else {
        payload.paymentMethod  = paymentForm.paymentMethod;
        payload.referenceNo    = paymentForm.referenceNo;
        if (paymentForm.paymentMethod === '2307') {
          payload.withheldAmount = parseFloat(paymentForm.withheldAmount) || 0;
        }
      }
      const res = await collectionAPI.addPayment(payload);
      const { collection, bill: updatedBill } = res.data.data;
      setCollections((prev) => [collection, ...prev]);
      if (updatedBill) {
        setForm((prev) => ({
          ...prev, status: updatedBill.status, IsReceived: updatedBill.IsReceived,
          totalCollected: updatedBill.totalCollected, outstanding: updatedBill.outstanding,
        }));
      }
      setPaymentForm({ transactionType: 'payment', deductionType: 'discount', withheldAmount: '', amount: '', paymentDate: today(), paymentMethod: 'cash', referenceNo: '', notes: '' });
      setShowPaymentForm(false);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save payment.');
      throw e; // let useGuardedMutation know this attempt failed — keeps the idempotency key for retry
    }
  });

  const handleDeletePayment = async (collectionId) => {
    if (!window.confirm('Delete this payment record? This will update the bill balance.')) return;
    try {
      const res = await collectionAPI.deletePayment(collectionId);
      setCollections((prev) => prev.filter((c) => c._id !== collectionId));
      if (res.data?.data?.bill) {
        const b = res.data.data.bill;
        setForm((prev) => ({ ...prev, status: b.status, IsReceived: b.IsReceived,
          totalCollected: b.totalCollected, outstanding: b.outstanding }));
      }
    } catch {
      setError('Failed to delete payment.');
    }
  };

  const handleRetryGL = async (collectionId) => {
    if (glActionPendingId) return;
    setGlActionPendingId(collectionId);
    try {
      const res = await collectionAPI.retryGLPost(collectionId);
      const updated = res.data?.data;
      if (updated) setCollections((prev) => prev.map((c) => c._id === collectionId ? { ...c, ...updated } : c));
    } catch (e) {
      const updated = e?.response?.data?.data;
      if (updated) setCollections((prev) => prev.map((c) => c._id === collectionId ? { ...c, ...updated } : c));
      setError(e?.response?.data?.message || 'GL retry failed.');
    } finally {
      setGlActionPendingId(null);
    }
  };

  const handleVoidPayment = async (collectionId) => {
    if (glActionPendingId) return;
    if (!window.confirm('Void this payment? A reversing GL entry will be created. This cannot be undone.')) return;
    setGlActionPendingId(collectionId);
    try {
      const res = await collectionAPI.voidCollection(collectionId);
      const updated = res.data?.data;
      if (updated) setCollections((prev) => prev.map((c) => c._id === collectionId ? { ...c, ...updated } : c));
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to void payment.');
    } finally {
      setGlActionPendingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-4 grid grid-cols-3 gap-4 text-center">
        <div>
          <p className="text-xs text-gray-400 uppercase mb-1">Amount Due</p>
          <p className="text-lg font-semibold text-gray-900">₱{PHP(amountDue)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 uppercase mb-1">Total Collected</p>
          <p className="text-lg font-semibold text-green-600">₱{PHP(totalCollected)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 uppercase mb-1">Outstanding</p>
          <p className={`text-lg font-semibold ${outstanding <= 0 ? 'text-green-600' : 'text-red-600'}`}>
            ₱{PHP(outstanding)}
          </p>
        </div>
      </div>

      {!isVoided && !form.isVoided && outstanding > 0 && (
        <button onClick={() => setShowPaymentForm((p) => !p)}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700">
          <PlusCircleIcon className="h-4 w-4" />
          {showPaymentForm ? 'Cancel' : 'Add Payment'}
        </button>
      )}

      {showPaymentForm && (
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 space-y-3">
          {/* Transaction type toggle */}
          <div className="flex gap-2">
            {[{ val: 'payment', label: 'Record Payment' }, { val: 'deduction', label: 'Apply Deduction' }].map(({ val, label }) => (
              <button key={val} type="button"
                onClick={() => setPaymentForm((p) => ({ ...p, transactionType: val }))}
                className={`px-3 py-1.5 text-sm rounded-lg font-medium border transition-colors ${
                  paymentForm.transactionType === val
                    ? val === 'payment' ? 'bg-teal-600 text-white border-teal-600' : 'bg-orange-500 text-white border-orange-500'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}>
                {label}
              </button>
            ))}
          </div>

          <h3 className={`text-sm font-semibold ${paymentForm.transactionType === 'deduction' ? 'text-orange-700' : 'text-teal-800'}`}>
            {paymentForm.transactionType === 'deduction' ? 'Apply Deduction / Adjustment' : 'Record Payment'}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Amount (₱) *</label>
              <input type="number" step="0.01" min="0.01" value={paymentForm.amount}
                onChange={(e) => setPaymentForm((p) => ({ ...p, amount: e.target.value }))}
                placeholder="0.00"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <div className="flex gap-2 mt-1">
                <button onClick={() => setPaymentForm((p) => ({ ...p, amount: outstanding.toFixed(2) }))}
                  className="text-xs text-teal-600 hover:underline">
                  Full (₱{PHP(outstanding)})
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Date *</label>
              <input type="date" value={paymentForm.paymentDate}
                onChange={(e) => setPaymentForm((p) => ({ ...p, paymentDate: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {paymentForm.transactionType === 'deduction' ? (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Deduction Type</label>
                <select value={paymentForm.deductionType}
                  onChange={(e) => setPaymentForm((p) => ({ ...p, deductionType: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
                  <option value="discount">Discount</option>
                  <option value="damage">Damage</option>
                  <option value="missing">Missing</option>
                  <option value="general_expense">General Expense</option>
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Payment Method</label>
                <select value={paymentForm.paymentMethod}
                  onChange={(e) => setPaymentForm((p) => ({ ...p, paymentMethod: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
                  <option value="cash">Cash</option>
                  <option value="check">Check</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="gcash">GCash</option>
                  <option value="2307">2307 (Withholding Tax)</option>
                  <option value="other">Other</option>
                </select>
              </div>
            )}

            {paymentForm.transactionType === 'payment' && paymentForm.paymentMethod === '2307' && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Withheld Amount (₱)</label>
                <input type="number" step="0.01" min="0" value={paymentForm.withheldAmount}
                  onChange={(e) => setPaymentForm((p) => ({ ...p, withheldAmount: e.target.value }))}
                  placeholder="0.00"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            )}

            {paymentForm.transactionType === 'payment' && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Reference No.</label>
                <input type="text" value={paymentForm.referenceNo}
                  onChange={(e) => setPaymentForm((p) => ({ ...p, referenceNo: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            )}

            <div className={paymentForm.transactionType === 'payment' ? 'sm:col-span-2' : 'sm:col-span-2'}>
              <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
              <input type="text" value={paymentForm.notes}
                onChange={(e) => setPaymentForm((p) => ({ ...p, notes: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-1">
            <button onClick={() => setShowPaymentForm(false)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={handleAddPayment} disabled={paymentSaving || !paymentForm.amount}
              className={`px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50 ${
                paymentForm.transactionType === 'deduction' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-teal-600 hover:bg-teal-700'
              }`}>
              {paymentSaving ? 'Saving…' : paymentForm.transactionType === 'deduction' ? 'Apply Deduction' : 'Save Payment'}
            </button>
          </div>
        </div>
      )}

      {collectionsLoading && <div className="text-center text-gray-400 py-8 text-sm">Loading…</div>}
      {!collectionsLoading && collections.length === 0 && (
        <div className="text-center text-gray-400 py-8 text-sm">No payments recorded yet.</div>
      )}
      {!collectionsLoading && collections.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Date', 'Amount', 'Type', 'Reference No.', 'Notes', 'Received By', 'GL', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {collections.map((c) => {
                const isDeduction = c.transactionType === 'deduction';
                const deductionLabels = { discount: 'Discount', damage: 'Damage', missing: 'Missing', general_expense: 'Gen. Expense' };
                const typeLabel = isDeduction
                  ? (deductionLabels[c.deductionType] || c.deductionType)
                  : (c.paymentMethod === '2307' ? '2307' : (c.paymentMethod?.replace(/_/g, ' ') || ''));
                return (
                  <tr key={c._id} className={`hover:bg-gray-50 ${c.isVoided ? 'opacity-60' : ''}`}>
                    <td className="px-4 py-3 whitespace-nowrap">{new Date(c.paymentDate).toLocaleDateString('en-PH')}</td>
                    <td className={`px-4 py-3 font-medium ${c.isVoided ? 'line-through text-gray-400' : isDeduction ? 'text-orange-600' : 'text-green-700'}`}>
                      ₱{PHP(c.amount)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                        isDeduction ? 'bg-orange-100 text-orange-700' : 'bg-teal-100 text-teal-700'
                      }`}>{typeLabel}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{c.referenceNo || '—'}</td>
                    <td className="px-4 py-3 text-gray-500">{c.notes || '—'}</td>
                    <td className="px-4 py-3 text-gray-500">{c.receivedByName || '—'}</td>
                    <td className="px-4 py-3">
                      <GLStatusBadge
                        status={c.glPostingStatus}
                        error={c.glPostingError}
                        collectionId={c._id}
                        isVoided={c.isVoided}
                        onRetry={handleRetryGL}
                        loading={glActionPendingId === c._id}
                        disabled={!!glActionPendingId}
                      />
                    </td>
                    <td className="px-4 py-3">
                      {!c.isVoided && c.glPostingStatus === 'posted' && (
                        <button onClick={() => handleVoidPayment(c._id)}
                          disabled={!!glActionPendingId}
                          className="px-2 py-1 text-xs font-medium text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Void this payment and create a reversing GL entry">
                          {glActionPendingId === c._id ? 'Voiding…' : 'Void'}
                        </button>
                      )}
                      {!c.isVoided && c.glPostingStatus !== 'posted' && (
                        <button onClick={() => handleDeletePayment(c._id)}
                          disabled={!!glActionPendingId}
                          className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-40 disabled:cursor-not-allowed" title="Delete">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function F({ label, value, onChange, type = 'text', placeholder = '' }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <input type={type} value={value} onChange={onChange} placeholder={placeholder}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
    </div>
  );
}
function CB({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input type="checkbox" checked={checked} onChange={onChange} className="h-4 w-4 text-teal-600 rounded" />
      {label}
    </label>
  );
}
function Btn({ label, onClick, disabled, primary }) {
  return (
    <button
      onClick={onClick} disabled={disabled}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
        primary ? 'bg-teal-600 text-white hover:bg-teal-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );
}
